import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { DEF_BY_NO } from '@/canvas/defs/index';
import { defaultAnswers, unconfirmedUpstream } from '@/canvas/engine';
import { formatDate } from '@/canvas/docmodel';
import { useCanvases } from '@/components/canvas/CanvasProvider';
import { FormProvider, StepBlocks } from '@/components/canvas/CanvasForm';
import ResultStrip from '@/components/canvas/ResultStrip';
import SignoffBlock from '@/components/canvas/SignoffBlock';
import CanvasHeader from '@/components/canvas/CanvasHeader';
import CanvasNotices from '@/components/canvas/CanvasNotices';
import ConfirmBar, { jumpTo } from '@/components/canvas/ConfirmBar';
import { canvasLabel, familyOf, stepSolid, stepText } from '@/components/canvas/bits';

export const route = { path: '/canvas/:n', layout: 'owner', access: "profile === 'Learner_c'" };

const toggled = (set, k) => {
  const next = new Set(set);
  if (next.has(k)) next.delete(k);
  else next.add(k);
  return next;
};

function CanvasPage({ no }) {
  const def = DEF_BY_NO[no];
  const ctx = useCanvases();
  const { loading, loadError, reload, needsWelcome, states, agg, setAnswer, ensureDefaults, confirmCanvas, reaffirm, reopen, reset, fillExample, flush } = ctx;
  const state = def ? states[no] : null;

  const [allGuides, setAllGuides] = useState(false);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [closedSet, setClosedSet] = useState(() => new Set());
  const [showMissing, setShowMissing] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    if (!loading && def) ensureDefaults(no);
  }, [loading, def, no, ensureDefaults]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    return () => {
      flush();
    };
  }, [no, flush]);

  const onValue = useCallback((k, val) => setAnswer(no, k, val), [setAnswer, no]);
  const up = useMemo(() => ({ ...states, agg }), [states, agg]);
  const missingSet = useMemo(() => new Set((state?.missing || []).map((m) => m.key)), [state?.missing]);

  const guides = useMemo(
    () => ({
      isOpen: (k) => (allGuides ? !closedSet.has(k) : openSet.has(k)),
      toggle: (k) => {
        if (allGuides) setClosedSet((p) => toggled(p, k));
        else setOpenSet((p) => toggled(p, k));
      },
    }),
    [allGuides, openSet, closedSet],
  );

  const formValue = useMemo(
    () =>
      state
        ? { no, def, state, up, v: state.v, calc: state.d.calc, disabled: state.confirmed, onValue, missing: missingSet, showMissing, guides }
        : null,
    [no, def, state, up, onValue, missingSet, showMissing, guides],
  );

  if (!def) {
    return (
      <div className="mx-auto max-w-xl space-y-4 text-center">
        <h1 className="font-heading text-2xl font-semibold">That canvas does not exist</h1>
        <p className="text-muted-foreground">The canvases are numbered 0 (the Case Card) to 9.</p>
        <Button asChild>
          <Link to="/dashboard">Back to Dashboard</Link>
        </Button>
      </div>
    );
  }
  if (needsWelcome) return <Navigate to="/welcome" replace />;
  if (loading || !state) {
    return (
      <div className="mx-auto max-w-5xl space-y-5" aria-busy="true">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="mx-auto max-w-xl space-y-4 rounded-2xl border border-destructive/40 bg-destructive/10 p-6">
        <h1 className="font-heading text-xl font-semibold">This canvas did not load</h1>
        <p className="text-sm">{loadError}</p>
        <Button onClick={reload}>Try again</Button>
      </div>
    );
  }

  const fam = familyOf(def);
  const label = canvasLabel(def);
  const behind = state.confirmed ? [] : unconfirmedUpstream(def, states);
  const defaults = defaultAnswers(def);
  const typed = Object.keys(state.v).some((k) => !k.startsWith('_') && state.v[k] !== defaults[k]);
  const missing = state.missing || [];
  const blockers = state.d.blockers || [];
  const warnings = state.d.warnings || [];
  const confirmedOn = formatDate(state.v._confirmedOn);
  const prev = DEF_BY_NO[no - 1];
  const next = DEF_BY_NO[no + 1];
  const howIsOpen = howOpen === null ? !typed && !state.confirmed : howOpen;

  const stepMissing = (id) => missing.filter((m) => m.step === id).length;

  async function onConfirm() {
    if (!state.canConfirm) {
      setShowMissing(true);
      if (missing.length > 0) {
        setMissingOpen(true);
        jumpTo(missing[0].key);
        toast.message(`${missing.length} required ${missing.length === 1 ? 'answer is' : 'answers are'} still empty`, {
          description: 'They are marked in red. Fill them in, then confirm.',
        });
      } else if (blockers.length > 0) {
        toast.message('One thing needs fixing before you can confirm', { description: blockers[0].text });
      }
      return;
    }
    setConfirming(true);
    try {
      const ok = await confirmCanvas(no);
      if (ok) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.success(`${label} confirmed`, { description: next ? `Next up: ${canvasLabel(next)}, ${next.title}.` : 'All ten are done. Your pack is ready.' });
      } else {
        toast.error('Could not save. Please try again.');
      }
    } finally {
      setConfirming(false);
    }
  }

  async function onReaffirm() {
    setConfirming(true);
    try {
      const ok = await reaffirm(no);
      if (ok) toast.success(`${label} confirmed again`);
      else toast.error('Fix what is marked first, then confirm again.');
    } finally {
      setConfirming(false);
    }
  }

  return (
    <FormProvider value={formValue}>
      <div className="mx-auto max-w-5xl space-y-6">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/dashboard">
            <ApperIcon name="ArrowLeft" size={16} /> Back to Dashboard
          </Link>
        </Button>

        <CanvasHeader
          def={def}
          state={state}
          typed={typed}
          allGuides={allGuides}
          onToggleGuides={() => {
            setAllGuides((a) => !a);
            setOpenSet(new Set());
            setClosedSet(new Set());
          }}
          onFillExample={() => {
            fillExample(no);
            toast.success('NorthStar example filled in. Change anything to make it yours.');
          }}
          onReset={() => setResetOpen(true)}
        />

        <CanvasNotices
          state={state}
          behind={behind}
          next={next}
          confirming={confirming}
          confirmedOn={confirmedOn}
          onReaffirm={onReaffirm}
          onReopen={() => reopen(no)}
        />

        {def.howTo && def.howTo.length > 0 && (
          <Collapsible open={howIsOpen} onOpenChange={setHowOpen} className="rounded-2xl border border-border bg-card">
            <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 rounded-2xl px-4 py-3 text-left text-sm font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
              <span className="flex items-center gap-2">
                <ApperIcon name="Route" size={16} className="text-primary" />
                How this canvas works
              </span>
              <ApperIcon name="ChevronDown" size={16} className="text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ol className="space-y-2 border-t border-border px-4 pb-4 pt-3">
                {def.howTo.map((line, i) => (
                  <li key={line} className="flex gap-2.5 text-sm leading-relaxed">
                    <span className={cn('mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white', fam.solid)}>{i + 1}</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ol>
            </CollapsibleContent>
          </Collapsible>
        )}

        <nav
          aria-label="Steps"
          className="sticky top-(--spacing-header) z-(--z-base) -mx-4 flex gap-2 overflow-x-auto border-b border-border bg-background/95 px-4 py-2.5 backdrop-blur md:-mx-6 md:px-6 lg:-mx-8 lg:px-8"
        >
          {def.steps.map((st, i) => {
            const done = stepMissing(st.id) === 0;
            return (
              <button
                key={st.id}
                type="button"
                onClick={() => document.getElementById(`step-${st.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="flex shrink-0 items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-3 text-xs font-medium outline-none transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <span className={cn('flex size-6 items-center justify-center rounded-full text-[11px] font-semibold', stepSolid(i), stepText(i))}>
                  {done && state.started ? <ApperIcon name="Check" size={13} /> : i + 1}
                </span>
                <span className="max-w-40 truncate">{st.title}</span>
              </button>
            );
          })}
        </nav>

        {def.steps.map((st, i) => {
          const left = stepMissing(st.id);
          return (
            <section key={st.id} id={`step-${st.id}`} className="scroll-mt-36 overflow-hidden rounded-2xl border border-border bg-card shadow-xs">
              <div className="flex items-start gap-3 border-b border-border p-4 md:p-5">
                <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl font-heading text-base font-semibold', stepSolid(i), stepText(i))}>{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <h2 className="font-heading text-base font-semibold leading-snug">{st.title}</h2>
                  {st.minutes ? <p className="text-xs text-muted-foreground">About {st.minutes} min</p> : null}
                </div>
                {left === 0 && state.started ? (
                  <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-success">
                    <ApperIcon name="CircleCheck" size={14} /> Done
                  </span>
                ) : (
                  left > 0 && <span className="shrink-0 text-xs text-muted-foreground">{left} still needed</span>
                )}
              </div>
              <div className="space-y-5 p-4 md:p-5">
                {st.hint && (
                  <div className="flex gap-2.5 rounded-xl bg-accent/70 p-3 text-sm leading-relaxed">
                    <ApperIcon name="ListChecks" size={18} className="mt-0.5 shrink-0 text-primary" />
                    <p>
                      <span className="font-semibold">What to do. </span>
                      {st.hint}
                    </p>
                  </div>
                )}
                <StepBlocks blocks={st.blocks} />
              </div>
            </section>
          );
        })}

        <ResultStrip results={def.results} values={state.d.results} />

        {(warnings.length > 0 || blockers.length > 0) && (
          <div className="space-y-2">
            {blockers.map((b, i) => (
              <div key={`b-${i}`} className="flex gap-2.5 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm" role="alert">
                <ApperIcon name="OctagonAlert" size={18} className="mt-0.5 shrink-0 text-destructive" />
                <p>
                  <span className="font-semibold">Fix before you confirm. </span>
                  {b.text}
                </p>
              </div>
            ))}
            {warnings.map((w, i) => (
              <div key={`w-${i}`} className="flex gap-2.5 rounded-xl border border-warning-border bg-warning-muted p-3 text-sm">
                <ApperIcon name="Lightbulb" size={18} className="mt-0.5 shrink-0 text-warning" />
                <p>
                  <span className="font-semibold">Worth a look. </span>
                  {w.text}
                </p>
              </div>
            ))}
          </div>
        )}

        <SignoffBlock def={def} />

        <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
          {prev ? (
            <Button variant="ghost" asChild>
              <Link to={`/canvas/${prev.no}`}>
                <ApperIcon name="ArrowLeft" size={16} /> {canvasLabel(prev)}
              </Link>
            </Button>
          ) : (
            <span />
          )}
          {next ? (
            <Button variant="ghost" asChild>
              <Link to={`/canvas/${next.no}`}>
                {canvasLabel(next)} <ApperIcon name="ArrowRight" size={16} />
              </Link>
            </Button>
          ) : (
            <Button variant="ghost" asChild>
              <Link to="/pack">
                My pack <ApperIcon name="ArrowRight" size={16} />
              </Link>
            </Button>
          )}
        </div>

        <ConfirmBar
          def={def}
          state={state}
          label={label}
          confirming={confirming}
          missingOpen={missingOpen}
          onToggleMissing={() => setMissingOpen((o) => !o)}
          onShowMissing={() => setShowMissing(true)}
          onConfirm={onConfirm}
          onReopen={() => reopen(no)}
        />
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start {label} again?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears everything you typed on this canvas and unlocks it if it was confirmed. Your other canvases are not touched, but any that pull from this one will ask for a review.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my answers</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={async () => {
                await reset(no);
                setShowMissing(false);
                setMissingOpen(false);
                toast.success(`${label} cleared`);
              }}
            >
              Clear this canvas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormProvider>
  );
}

export default function Canvas() {
  const { n } = useParams();
  const no = Number(n);
  return <CanvasPage key={String(n)} no={Number.isInteger(no) ? no : -1} />;
}
